import { useQuery } from "@tanstack/react-query";
import axios from "./axios";

export const TCOKeys = {
  month: (fleetId: number) => ["tco-month", "idFleet", fleetId] as const,
  vehicles: (fleetId: number) => ["tco-vehicles", "idFleet", fleetId] as const,
  tcoByVehicleMonthly: (vehicleId: number) =>
    ["tco-month-vehicle", "idVehicle", vehicleId] as const,
  tcoByEmployee: (fleetId: number, userId: number) =>
    ["tco-employee", "idFleet", fleetId, "idUser", userId] as const,
};

/** True for a usable path segment. `/fleets/0/...` and `/fleets/NaN/...` both 400. */
const isValidId = (id: unknown): boolean =>
  typeof id === "number" && Number.isFinite(id) && id > 0;

export const useGetVehiclesByStatus = (fleetId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: TCOKeys.vehicles(fleetId),
    queryFn: async (): Promise<TVehicleByStatus[]> => {
      const data = await axios.get(
        `/fleets/${fleetId}/fleet-vehicle-by-status`
      );
      return data?.data;
    },
    enabled: isValidId(fleetId),
  });

  return { vehiclesByStatus: data, ...res };
};

export const useGetTCOByCompany = (fleetId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: TCOKeys.month(fleetId),
    queryFn: async (): Promise<TCO> => {
      const data = await axios.get(`/companies/${fleetId}/tco-by-company`);
      return data?.data;
    },
    enabled: isValidId(fleetId),
  });

  return { tco: data, ...res };
};

/**
 * TCO for a single vehicle.
 *
 * The `enabled` guard matters here: this hook is called from the TCO page
 * before any vehicle has been chosen, and without it react-query would request
 * `/companies/{fleet}/tco-by-vehicles/0` on mount and surface the resulting
 * error as though the page were broken.
 */
export const useGetTotalTCOByVehicle = (fleetId: number, vehicleId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: TCOKeys.tcoByVehicleMonthly(vehicleId),
    queryFn: async (): Promise<TCO> => {
      const data = await axios.get(
        `/companies/${fleetId}/tco-by-vehicles/${vehicleId}`
      );
      return data?.data;
    },
    enabled: isValidId(fleetId) && isValidId(vehicleId),
  });

  return { totalTcoByVehicle: data, ...res };
};

/**
 * TCO aggregated over the vehicles assigned to one employee.
 *
 * `userId` is the user id, matching /fleets/{id}/employees/{id}/vehicles --
 * the API resolves the company_user row itself.
 *
 * The server returns zeros rather than 404 when the employee holds no vehicle
 * under an active contract, so an empty result is a real answer and not an
 * error state to render.
 */
export const useGetTCOByEmployee = (fleetId: number, userId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: TCOKeys.tcoByEmployee(fleetId, userId),
    queryFn: async (): Promise<TCOByEmployee> => {
      const data = await axios.get(
        `/companies/${fleetId}/tco-by-employee/${userId}`
      );
      return data?.data;
    },
    enabled: isValidId(fleetId) && isValidId(userId),
  });

  return { tcoByEmployee: data, ...res };
};
